"use client";

import {CardElement, Elements, useElements, useStripe} from '@stripe/react-stripe-js';
import {Stripe, StripeElementsOptionsMode} from '@stripe/stripe-js';
import { loadStripe } from "@stripe/stripe-js/pure";
import { FormEvent, useEffect, useState } from 'react';
import { Button } from './ui/Button';
import { EventType, isTokenizePaymentMessage } from '@vertical-insure/shared';
import { useOfferContext } from '@/lib/hooks/useOfferContext';
import { useFormContext } from 'react-hook-form';
import { completePurchase } from '@/lib/actions';
import { useRouter } from 'next/navigation'
import { datadogRum } from '@datadog/browser-rum';

export const PaymentForm = () => {
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const { config, getSelectedQuotes } = useOfferContext();
  const stripe = useStripe();
  const elements = useElements();
  const form = useFormContext();
  const router = useRouter();
  const isTest = config?.is_test ?? true;

  useEffect(() => {
    const handler = async (ev: MessageEvent<unknown>) => {
      if (isTokenizePaymentMessage(ev.data)) {
        tokenize();
      }
    }

    window.addEventListener('message', handler);

    return () => window.removeEventListener('message', handler)
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const results = await tokenize();

    if (results?.token) {
      const formData = new FormData(e.target as HTMLFormElement);
      formData.append('payment_token', results.token);
      for (const value of formData.values()) {
        console.log(value);
      }
      const res = await completePurchase(formData);
      if ('error' in res) {
        setError(res.error);
        datadogRum.addError(res.error, {
          message: 'Error completing purchase',
          metadata: {
            error: res.error,
            form: formData
          }
        });
        return;
      } else {
        const message = {
          eventType: EventType.offer_state_change,
          quotes: res,
        };

        window.parent.postMessage(message, '*');
        router.push('/v1/purchase-complete');
        return;
      }
    } 
    setLoading(false);
  }

  const tokenize = async () => {
    if (config?.origin) {
      setError(undefined);
      
      const valid = await form.trigger();

      if (!valid) {
        window.parent.postMessage({
          eventType: EventType.tokenize_payment,
          is_valid: false
        }, config.origin);
        setLoading(false);
        return;
      }

      if (!stripe || !elements) {
        return {
          error: 'No payment element available.'
        };
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        elements
      });

      if (error) {
        window.parent.postMessage({
          eventType: EventType.tokenize_payment,
          error: {
            code: error.type,
            message: error.message
          }
        }, config.origin);

        setError(error.message);

        datadogRum.addError(error.message, {
          message: 'Error tokenizing payment',
          metadata: {
            error
          }
        });

        return {
          error: error.message
        };
      }

      window.parent.postMessage({
        eventType: EventType.tokenize_payment,
        token: paymentMethod.id
      }, config.origin);

      return {
        token: `stripe:${paymentMethod.id}`
      }
    }
  }

  // grab selected quotes
  const acceptedQuoteIds = form?.getValues('products')
    .map((p: { accepted_quotes: string[]; }) => p.accepted_quotes).flat();
  const selectedQuotes = getSelectedQuotes(acceptedQuoteIds);
  
  return (
    <form className="space-y-2" id="payment-form" onSubmit={handleSubmit}>
      <div className="mt-2 border rounded px-2 py-3">
        <CardElement />
      </div>
      {error &&
        <div className="text-sm py-1 text-error">{error}</div>
      }
      {config?.payments?.button && 
        <>
          <input type="hidden" name="product_quotes" value={JSON.stringify(selectedQuotes)} />
          <input type="hidden" name="is_test" value={isTest.toString()} />
          <div className="flex justify-end">
            <Button disabled={!stripe || loading} isLoading={loading}>Purchase Coverage</Button>
          </div>
        </>
      }
    </form>
  )
}

export const PaymentComponent = () => {
  const [stripeObject, setStripeObject] = useState<Promise<Stripe | null> | null>(null);
  const { config, findFirstQuote } = useOfferContext();
  const anyQuote = findFirstQuote();
  const isTest = config?.is_test;

  useEffect(() => {
    const fetchStripeObject = async () => {
      if (anyQuote) {  
        const stripePublishableKey = 
          isTest ? process.env.NEXT_PUBLIC_STRIPE_US_TEST_PUBLISHABLE_KEY! : process.env.NEXT_PUBLIC_STRIPE_US_LIVE_PUBLISHABLE_KEY!;
          
        const res = loadStripe(stripePublishableKey);
        // When we have got the Stripe object, pass it into our useState.
        setStripeObject(res);
      }
    };
    fetchStripeObject();
  }, [anyQuote, isTest]);

  if (!anyQuote) { 
    return null;
  }

  const stripeConfig: StripeElementsOptionsMode = {
    mode: 'setup',
    currency: anyQuote.currency.toLowerCase(),
    appearance: {
      variables: {
        colorPrimary: config?.theme?.colors?.primary,
        colorDanger: config?.theme?.colors?.error,
        fontFamily: config?.theme?.font_family
      }
    }
  };
  
  return (
    <Elements stripe={stripeObject} options={stripeConfig}>
      <PaymentForm />
    </Elements>
  )
}